import { useEffect, useState, useRef, useCallback } from "react";

interface LiveEvent {
  event: string;
  run_id: string;
  [key: string]: unknown;
}

/**
 * Accumulates SSE events for a specific run into an array.
 * Returns all events received since the hook mounted.
 * Call clear() to reset (e.g., when switching runs).
 */
export function useLiveEvents(url: string, runId: string | undefined): {
  events: LiveEvent[];
  clear: () => void;
} {
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const runIdRef = useRef(runId);

  // Clear when run changes
  if (runIdRef.current !== runId) {
    runIdRef.current = runId;
    setEvents([]);
  }

  const clear = useCallback(() => setEvents([]), []);

  useEffect(() => {
    if (!url || !runId) return;

    const es = new EventSource(url, { withCredentials: true });

    const eventNames = [
      "run:testCaseStarted",
      "run:testStepFinished",
      "run:testCaseFinished",
      "run:execution",
    ];

    const handler = (e: Event) => {
      try {
        const data = JSON.parse((e as MessageEvent).data) as LiveEvent;
        // Only accumulate events for our run
        if (data.run_id === runId) {
          setEvents((prev) => [...prev, { ...data, event: e.type }]);
        }
      } catch {
        // ignore
      }
    };

    for (const name of eventNames) {
      es.addEventListener(name, handler);
    }

    return () => {
      for (const name of eventNames) {
        es.removeEventListener(name, handler);
      }
      es.close();
    };
  }, [url, runId]);

  return { events, clear };
}
